import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  ArrowUpRight,
  Loader2,
  MapPin,
  Search,
  UserRound,
  X,
} from 'lucide-react';
import { request, toFullMediaUrl } from '../../axios_helper';
import { useLanguage } from '../../context/LanguageContext';
import { getDisplayName } from '../../utils/userDisplay';
import { richTextToPlainText } from '../../utils/richText';
import parseGlobalSearchQuery from '../../utils/globalSearch';
import { formatPlace, toEnglishCity, toEnglishCountry } from '../../utils/localization';
import Avatar from './Avatar';
import './global-search.css';

const toContent = (data) => {
  if (Array.isArray(data?.content)) return data.content;
  if (Array.isArray(data)) return data;
  return [];
};

const tripLocation = (trip, language = 'pt') => {
  const cities = Array.isArray(trip?.citiesVisited)
    ? trip.citiesVisited.filter(Boolean)
    : [trip?.city, trip?.cityName].filter(Boolean);
  const countries = Array.isArray(trip?.countriesVisited)
    ? trip.countriesVisited.filter(Boolean)
    : [trip?.country, trip?.countryName].filter(Boolean);
  return formatPlace([cities[0], countries[0]].filter(Boolean).join(', '), language);
};

const tripSummary = (trip) => richTextToPlainText(
  trip?.tripSummary || trip?.summary || '',
).replace(/\s+/g, ' ').trim();

const userName = (person) => getDisplayName({
  firstName: person?.firstName,
  lastName: person?.lastName,
  username: person?.username,
}) || person?.username || 'Viajante';

const GlobalSearch = ({ open, onClose, onNavigate }) => {
  const { language } = useLanguage();
  const inputRef = useRef(null);
  const searchRequestRef = useRef(0);
  const searchDebounceRef = useRef(null);
  const [query, setQuery] = useState('');
  const [searchedQuery, setSearchedQuery] = useState('');
  const [trips, setTrips] = useState([]);
  const [travellers, setTravellers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    const focusTimer = window.setTimeout(() => inputRef.current?.focus(), 0);
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      searchRequestRef.current += 1;
      window.clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
      setQuery('');
      setSearchedQuery('');
      setTrips([]);
      setTravellers([]);
      setHasSearched(false);
      setLoading(false);
      setError('');
    }
  }, [open]);

  const runSearch = useCallback(async (value) => {
    window.clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = null;
    const rawQuery = String(value || '')
      .replace(/\s+/g, ' ')
      .trim();
    if (!rawQuery) {
      setHasSearched(false);
      setTrips([]);
      setTravellers([]);
      setLoading(false);
      setError('');
      return null;
    }

    const parsed = parseGlobalSearchQuery(rawQuery);
    // Search values are stored canonically in English by the API.  Accept
    // Portuguese city/country names in the UI and translate only the query
    // sent to the endpoint; the original query remains visible in the dialog.
    const apiParsed = {
      ...parsed,
      text: toEnglishCity(parsed.text),
      country: toEnglishCountry(parsed.country),
    };
    const tripParams = new URLSearchParams({ page: '0', size: '8', sort: 'id,desc' });
    if (apiParsed.text) tripParams.set('text', apiParsed.text);
    if (apiParsed.country) tripParams.set('countries', apiParsed.country);
    const userParams = new URLSearchParams({ query: parsed.raw, page: '0', size: '8' });
    const requestId = ++searchRequestRef.current;

    setLoading(true);
    setHasSearched(true);
    setSearchedQuery(rawQuery);
    setError('');

    const [tripResult, userResult] = await Promise.allSettled([
      request('GET', `/trips/public-feed?${tripParams.toString()}`),
      request('GET', `/users/search?${userParams.toString()}`),
    ]);

    // Ignore a slower previous search (or a request that finished after the
    // dialog was closed) so stale results can never replace a newer query.
    if (requestId !== searchRequestRef.current) return;

    const nextTrips = tripResult.status === 'fulfilled' ? toContent(tripResult.value?.data) : [];
    const nextTravellers = userResult.status === 'fulfilled' ? toContent(userResult.value?.data) : [];
    setTrips(nextTrips);
    setTravellers(nextTravellers);
    if (tripResult.status === 'rejected' && userResult.status === 'rejected') {
      setError('Não foi possível concluir a pesquisa. Tenta novamente.');
    }
    setLoading(false);
    return null;
  }, []);

  useEffect(() => {
    if (!open || !query.trim()) return undefined;

    searchDebounceRef.current = window.setTimeout(() => {
      searchDebounceRef.current = null;
      runSearch(query);
    }, 260);

    return () => {
      window.clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
      // Invalidate any request for the previous input as soon as the user
      // types again, so slower responses cannot replace newer results.
      searchRequestRef.current += 1;
    };
  }, [open, query, runSearch]);

  if (!open) return null;

  const handleQueryChange = (value) => {
    setQuery(value);
    searchRequestRef.current += 1;
    const normalized = value.replace(/\s+/g, ' ').trim();
    setSearchedQuery(normalized);
    setTrips([]);
    setTravellers([]);
    setError('');
    if (normalized) {
      setHasSearched(true);
      setLoading(true);
    } else {
      setHasSearched(false);
      setLoading(false);
    }
  };

  const goTo = (path) => {
    onClose();
    if (onNavigate) onNavigate(path);
  };

  return (
    <div className="gm-global-search" role="dialog" aria-modal="true" aria-label="Pesquisa global">
      <button className="gm-global-search__backdrop" type="button" aria-label="Fechar pesquisa" onClick={onClose} />
      <section className="gm-global-search__panel">
        <div className="gm-global-search__header">
          <div>
            <p className="gm-global-search__eyebrow">Pesquisar</p>
            <h2>Encontra viagens e viajantes</h2>
          </div>
          <button className="gm-global-search__close" type="button" onClick={onClose} aria-label="Fechar pesquisa">
            <X size={21} />
          </button>
        </div>

        <form className="gm-global-search__form" role="search" onSubmit={(event) => { event.preventDefault(); runSearch(query); }}>
          <Search size={20} aria-hidden="true" />
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => handleQueryChange(event.target.value)}
            placeholder="Pesquisar viagens, pontos de referência, itinerários ou viajantes..."
            aria-label="Pesquisar viagens, lugares ou viajantes"
            autoComplete="off"
          />
          {query && (
            <button type="button" className="gm-global-search__clear" onClick={() => setQuery('')} aria-label="Limpar pesquisa">
              <X size={16} />
            </button>
          )}
          {loading && <Loader2 size={17} className="gm-global-search__spinner" aria-label="A pesquisar" />}
        </form>

        {!hasSearched && (
          <div className="gm-global-search__hint">
            <p>Pesquisa viagens, lugares, pontos de referência, itinerários, categorias e viajantes em toda a Globe Memories.</p>
          </div>
        )}

        {hasSearched && (
          <div className="gm-global-search__results">
            <div className="gm-global-search__result-heading">
              <span>Resultados para “{searchedQuery}”</span>
              {searchedQuery.toLowerCase().includes(' em ') && (
                <small>O país foi aplicado automaticamente aos filtros de viagens.</small>
              )}
            </div>
            {error && <p className="gm-global-search__error">{error}</p>}

            {travellers.length > 0 && (
              <section className="gm-global-search__group">
                <div className="gm-global-search__group-title"><UserRound size={17} /> Viajantes</div>
                {travellers.map((person) => (
                  <button
                    type="button"
                    className="gm-global-search__result"
                    key={person.id || person.username}
                    onClick={() => goTo(`/profile/${encodeURIComponent(person.username || person.id)}`)}
                  >
                    <Avatar src={toFullMediaUrl(person.profilePhoto) || person.profilePhoto} name={userName(person)} size="sm" />
                    <span className="gm-global-search__result-copy">
                      <strong>{userName(person)}</strong>
                      <small>@{person.username}</small>
                    </span>
                    <ArrowUpRight size={16} />
                  </button>
                ))}
              </section>
            )}

            {trips.length > 0 && (
              <section className="gm-global-search__group">
                <div className="gm-global-search__group-title"><MapPin size={17} /> Viagens</div>
                {trips.map((trip) => (
                  <button
                    type="button"
                    className="gm-global-search__result"
                    key={trip.tripId || trip.id}
                    onClick={() => goTo(`/travel/${trip.tripId || trip.id}`)}
                  >
                    <span className="gm-global-search__trip-thumb">
                      {trip.tripPhoto ? <img src={toFullMediaUrl(trip.tripPhoto) || trip.tripPhoto} alt="" /> : <MapPin size={18} />}
                    </span>
                    <span className="gm-global-search__result-copy">
                      <strong>{trip.tripTitle || 'Viagem sem título'}</strong>
                      <small>{tripLocation(trip, language) || tripSummary(trip) || 'Ver detalhes da viagem'}</small>
                    </span>
                    <ArrowUpRight size={16} />
                  </button>
                ))}
              </section>
            )}

            {!loading && !error && trips.length === 0 && travellers.length === 0 && (
              <div className="gm-global-search__empty">Não encontrámos viagens ou viajantes para esta pesquisa.</div>
            )}

            <button
              type="button"
              className="gm-global-search__browse"
              onClick={() => goTo(`/travels?search=${encodeURIComponent(searchedQuery)}`)}
            >
              Ver resultados completos em Descobrir <ArrowUpRight size={16} />
            </button>
          </div>
        )}
      </section>
    </div>
  );
};

GlobalSearch.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onNavigate: PropTypes.func,
};

GlobalSearch.defaultProps = {
  open: false,
  onNavigate: null,
};

export default GlobalSearch;
